<script setup lang="ts">
import type { Component } from "vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import ExclamationCircle from "../../assets/exclamation-circle.svg?component";

interface Props {
  title?: string
  text?: string | ((errorCode?: number | null) => string | undefined)
  errorCode?: number | null
  iconComponent?: Component | null
  defaultStyleClasses?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  text: undefined,
  errorCode: null,
  iconComponent: () => ExclamationCircle,
  defaultStyleClasses: () => ["p-6"],
});
const { t, te } = useI18n();
const renderedTitle = computed(() => {
  return props.title || (props.errorCode && props.errorCode === 403 ? t("base.error_403_title") : t("base.error"));
});

const renderedText = computed(() => {
  const propText = typeof props.text === "function" ? props.text(props.errorCode) : props.text;
  const errorText = props.errorCode ? te(`base.${props.errorCode}`) ? t(`base.${props.errorCode}`) : t("base.unknown_code", { errorCode: props.errorCode }) : t("base.error_subtitle");

  if (props.text) {
    return propText;
  }

  if (props.errorCode) {
    return errorText;
  }

  return "";
});
</script>

<template>
  <div
    class="flex w-full flex-col items-center justify-center gap-4 text-(--text) text-center"
    :class="props.defaultStyleClasses"
  >
    <div v-if="props.iconComponent !== null" class="icon" aria-hidden="true">
      <slot name="icon">
        <component :is="props.iconComponent" />
      </slot>
    </div>
    <div v-if="renderedTitle !== ''" class="font-medium text-lg">
      <slot name="title">
        {{ renderedTitle }}
      </slot>
    </div>
    <div v-if="renderedText !== ''" class="text">
      <slot name="text">
        {{ renderedText }}
      </slot>
    </div>
  </div>
</template>
